import axios, { AxiosError, AxiosResponse } from "axios";
import humps from "humps";
import { Category } from "@/types/category";
import { Product } from "@/types/product";
import {
  CartRequestBody,
  Receipt,
  ReceiptRequestBody,
  CalculateCartResponse,
} from "@/types/receipt";
import { CashRegister } from "@/types/shop";
import useInternetConnectionStore from "@/stores/internet-connection";
import i18next from "i18next";
import { TimeSlot } from "@/types/timeSlot";
import Honeybadger from "@honeybadger-io/js";

export const API_ROOT =
  import.meta.env?.VITE_API_ROOT ?? "https://valktogo-api-staging.valkdigitalservices.nl";

export interface DataResponse<T> {
  data: T;
}

export const client = axios.create({
  baseURL: `${API_ROOT}/kiosk/api/v1`,
});

client.interceptors.response.use(
  (response) => {
    if (response.data && response?.headers["content-type"]?.includes("application/json")) {
      response.data = humps.camelizeKeys(response.data);
    }
    return response;
  },
  (error) => {
    error.response.data = humps.camelizeKeys(error.response.data);
    return Promise.reject(error);
  }
);

client.interceptors.request.use((config) => {
  const newConfig = { ...config };
  const kioskId = sessionStorage.getItem("kioskId");

  newConfig.headers["X-Kiosk-Language"] = i18next.language;
  if (kioskId) newConfig.headers["X-Kiosk-Id"] = kioskId;

  if (config.params) newConfig.params = humps.decamelizeKeys(config.params);
  if (config.data) newConfig.data = humps.decamelizeKeys(config.data);
  return newConfig;
});

const notifyHoneybadgerOfAxiosError = (e: AxiosError) => {
  Honeybadger.notify("Axios Error", {
    params: {
      request: JSON.stringify(e.request),
      response: JSON.stringify(e.response),
    },
  });
};

const handleErrors = (e: AxiosError) => {
  const statusCode = e.response?.status;
  const isCartEndpoint = e.config?.url?.includes("/cart");

  // `422` errors from the `/cart` endpoint are presented to the user
  if (!(statusCode === 422 && isCartEndpoint)) {
    notifyHoneybadgerOfAxiosError(e);
  }

  useInternetConnectionStore
    .getState()
    .setShowConnectionError(!window.navigator.onLine && e.code === "ERR_NETWORK");
  throw e;
};

const requests = {
  get: async <T>(url: string, config?: any): Promise<AxiosResponse<T>> => {
    try {
      return await client.get(url, config);
    } catch (err) {
      return handleErrors(err as AxiosError);
    }
  },
  post: async <T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> => {
    try {
      return await client.post(url, data, config);
    } catch (err) {
      return handleErrors(err as AxiosError);
    }
  },
  put: async <T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> => {
    try {
      return await client.put(url, data, config);
    } catch (err) {
      return handleErrors(err as AxiosError);
    }
  },
  delete: async <T>(url: string, config?: any): Promise<AxiosResponse<T>> => {
    try {
      return await client.delete(url, config);
    } catch (err) {
      return handleErrors(err as AxiosError);
    }
  },
};

class ApiService {
  client = requests;

  categories = {
    getAll: async () => this.client.get<DataResponse<Category[]>>("/categories"),
  };

  products = {
    getAll: async () => this.client.get<DataResponse<Product[]>>("/products"),
  };

  cashRegisters = {
    getCurrent: async () => this.client.get<DataResponse<CashRegister>>("/cash_registers/me"),
  };

  cart = {
    calculate: async (data: CartRequestBody) =>
      this.client.post<DataResponse<CalculateCartResponse>>("/cart", data),
  };

  receipts = {
    create: async (data: ReceiptRequestBody) =>
      this.client.post<DataResponse<Receipt>>("/receipts", data),
    update: async (id: number, data: ReceiptRequestBody) =>
      this.client.put<DataResponse<Receipt>>(`/receipts/${id}`, data),
    delete: async (id: Receipt["id"], reason: string) =>
      this.client.delete(`/receipts/${id}`, { params: { reason } }),
    pay: async (id: Receipt["id"]) =>
      this.client.post<DataResponse<Receipt>>(`/receipts/${id}/pay`),
  };

  email = {
    deliver: async (id: Receipt["id"], email: string) =>
      this.client.put<string>(`/receipts/${id}/deliver`, { email }),
  };

  timeSlots = {
    get: async () => this.client.get<DataResponse<TimeSlot[]>>("/pickup_times"),
  };
}

export default new ApiService();
